import { GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { useMessage } from "../../context/useMessageContext"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useTranslation } from "react-i18next"
import { CompanyMetaSettingsEntity } from "../../Objects/Meta/CompanyMetaSettingsEntity"
import { SaveMetaIntegrationPayload } from "./useCreateMetaIntegration"

interface TestMetaConnectionPayload {
  accountSid: string
  authToken: string
}

interface SendMetaWhatsappPayload {
  toPhone: string
  messageBody: string
  templateKey?: string
  tokenValues?: Record<string, string>
}

interface RegisterMetaPhoneNumberPayload {
  phoneNumberId: string
  authToken: string
  pin: string
}

interface CheckMetaPhoneNumberStatusPayload {
  phoneNumberId: string
  authToken: string
}

interface SyncMetaTemplatePayload {
  templateKey: string
  templateBody: string
  authToken: string
}

interface MetaTemplateStatusPayload {
  status: string
  reason?: string
  isSendAllowed: boolean
}

export interface UpdateMetaNotificationPreferencesPayload {
  isStatusChangedNotificationEnabled: boolean
  isFifteenDaysCampaignEnabled: boolean
  isPrizeNotificationEnabled: boolean
}

export const useMetaSave = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const { setMessage } = useMessage()

  const handleGetMetaIntegration = async (onSuccess: (data: CompanyMetaSettingsEntity) => void) => {
    enableLoad()

    try {
      const response = await GetRequest<CompanyMetaSettingsEntity>(API_ROUTES.META_INTEGRATION.GET)

      if (response.success && response.data) {
        onSuccess(new CompanyMetaSettingsEntity(response.data))
        return
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleSave = async (metaIntegration: SaveMetaIntegrationPayload, onSuccess?: () => void) => {
    enableLoad()

    try {
      const response = await PutRequest(API_ROUTES.META_INTEGRATION.SAVE, metaIntegration)

      if (response.success) {
        setMessage(t("label_title_success"), t("company:label_meta_saved_success"))

        if (onSuccess) {
          onSuccess()
        }

        return
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleTestConnection = async (
    payload: TestMetaConnectionPayload,
    onSuccess?: (message: string) => void,
    onError?: (message: string) => void,
  ) => {
    enableLoad()

    try {
      const response = await PostRequest(API_ROUTES.META_INTEGRATION.TEST_CONNECTION, payload)

      if (response.success) {
        setMessage(t("label_title_success"), response.message)

        if (onSuccess) {
          onSuccess(response.message)
        }

        return
      }

      if (onError) {
        onError(response.message)
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleSendWhatsappTest = async (payload: SendMetaWhatsappPayload, onSuccess?: () => void) => {
    enableLoad()

    try {
      const response = await PostRequest(API_ROUTES.META_INTEGRATION.SEND_WHATSAPP, payload)

      if (response.success) {
        setMessage(t("label_title_success"), t("company:label_meta_test_send_success"))

        if (onSuccess) {
          onSuccess()
        }

        return
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleToggle = async (isEnabled: boolean, onSuccess?: () => void) => {
    enableLoad()

    try {
      const response = await PutRequest(API_ROUTES.META_INTEGRATION.TOGGLE, { isEnabled })

      if (response.success) {
        if (onSuccess) {
          onSuccess()
        }

        return
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleUpdateNotificationPreferences = async (
    payload: UpdateMetaNotificationPreferencesPayload,
    onSuccess?: () => void,
  ) => {
    try {
      const response = await PutRequest(API_ROUTES.META_INTEGRATION.UPDATE_NOTIFICATION_PREFERENCES, payload)

      if (response.success) {
        if (onSuccess) {
          onSuccess()
        }

        return
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    }
  }

  const handleRegisterPhoneNumber = async (
    payload: RegisterMetaPhoneNumberPayload,
    onSuccess?: (data: any) => void,
  ) => {
    enableLoad()

    try {
      const response = await PostRequest<any>(API_ROUTES.META_INTEGRATION.REGISTER_PHONE_NUMBER, payload)

      if (response.success) {
        setMessage(t("label_title_success"), t("company:label_meta_phone_register_success"))

        if (onSuccess) {
          onSuccess(response.data)
        }

        return
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleCheckPhoneNumberStatus = async (
    payload: CheckMetaPhoneNumberStatusPayload,
    onSuccess?: (data: any) => void,
  ) => {
    enableLoad()

    try {
      const response = await PostRequest<any>(API_ROUTES.META_INTEGRATION.CHECK_PHONE_NUMBER_STATUS, payload)

      if (response.success) {
        if (onSuccess) {
          onSuccess(response.data)
        }

        return
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleGetTemplateStatuses = async (onSuccess?: (data: Record<string, MetaTemplateStatusPayload>) => void) => {
    enableLoad()

    try {
      const response = await GetRequest<Record<string, MetaTemplateStatusPayload>>(
        API_ROUTES.META_INTEGRATION.TEMPLATE_STATUSES,
      )

      if (response.success && response.data) {
        if (onSuccess) {
          onSuccess(response.data)
        }

        return
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleSyncTemplate = async (payload: SyncMetaTemplatePayload, onSuccess?: (data: any) => void) => {
    enableLoad()

    try {
      const response = await PostRequest<any>(API_ROUTES.META_INTEGRATION.SYNC_TEMPLATE, payload)

      if (response.success) {
        setMessage(t("label_title_success"), t("company:label_meta_template_sync_success"))

        if (onSuccess) {
          onSuccess(response.data)
        }

        return
      }

      throw new Error(response.message || t("label_unknown_error_description"))
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  return {
    handleGetMetaIntegration,
    handleSave,
    handleTestConnection,
    handleToggle,
    handleUpdateNotificationPreferences,
    handleSendWhatsappTest,
    handleRegisterPhoneNumber,
    handleCheckPhoneNumberStatus,
    handleGetTemplateStatuses,
    handleSyncTemplate,
  }
}
